import { DefaultType, Property, PropertyType } from 'api/clients/PropertyClient'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useSelector } from 'react-redux'
import { RootState } from 'types'

type ReturnType = {
  currentWorkspaceProperties: Property[]
  currentFolderProperties: Property[]
  propertiesUsableInCurrentFolder: Property[]
  projectProperties: (projectId: string) => Property[]
  fromStore: (properties: Property[]) => Property[]
  filterByName: (properties: Property[], query: string) => Property[]
  sortByName: (properties: Property[]) => Property[]
  dateProperties: Property[]
}

export const useProperties = (currentFolderId?: string): ReturnType => {
  const workspace = useCurrentWorkspace()
  const { currentFolder: folder } = useCurrentFolderStore()
  const ancestorAndCurrentKeys = folder && (folder.ancestor_keys || []).concat(folder.id)

  const allStorePropertiesById = useSelector(({ properties }: RootState) => properties)
  // given an array of properties, return the properties from store having the same IDs
  // that is to always get the data from the source of truth for properties, which is the redux store.
  const fromStore = (inputProperties: Property[]) => {
    // keep inputProperties in order, but get the freshest data from redux store
    // remove undefined, so until store is populated, response is empty array.
    return inputProperties.map(({ id }) => allStorePropertiesById[id]).filter((p) => p)
  }

  // all properties whose workspace_id is current workspace
  // includes folder properties
  const allPropertiesInCurrentWorkspace = useSelector(({ properties }: RootState) => {
    return Object.values(properties).filter(({ workspace_id }) => workspace_id === workspace?.id)
  })
  // workspace properties,
  // includes "system" properties (= default properties)
  // excludes folder properties,
  const currentWorkspaceProperties = allPropertiesInCurrentWorkspace.filter(({ scope_project_id }) => !scope_project_id)

  // properties scoped to current folder or a parent,
  // excludes workspace properties,
  // excludes "system" properties (= default properties)
  const currentFolderProperties = allPropertiesInCurrentWorkspace.filter(
    ({ scope_project_id, workflow_project_id }) => {
      if (currentFolderId) {
        return scope_project_id === currentFolderId
      }
      const notAnAncestorWorkflowProperty = folder && (!workflow_project_id || workflow_project_id === folder.id)
      const isfolderPropertyFromCurrentTree = scope_project_id && ancestorAndCurrentKeys?.includes(scope_project_id)
      return isfolderPropertyFromCurrentTree && notAnAncestorWorkflowProperty
    },
  )


  const unavailableWorkspaceProperties: any = [DefaultType.Assignee, DefaultType.LastOpened, DefaultType.Shared]
  // workspace properties + properties scoped to current folder or a parent
  // we are filtering out Assignee from everywhere since it's not working as we want it to be
  const propertiesUsableInCurrentFolder = currentWorkspaceProperties
    .concat(currentFolderProperties)
    .filter(({ default_type }) => unavailableWorkspaceProperties.indexOf(default_type) === -1)

  const filterByName = (properties: Property[], query: string) => {
    return properties.filter(({ name }) => !query.length || name.toLowerCase().includes(query.toLowerCase()))
  }

  const sortByName = (properties: Property[]) => {
    return properties.sort((a, b) => a.name.localeCompare(b.name))
  }

  const projectProperties = (projectId: string) => {
    return allPropertiesInCurrentWorkspace.filter(
      ({ scope_project_id }) => scope_project_id === projectId,
    )
  }

  const dateProperties = propertiesUsableInCurrentFolder.filter((p) => !p.is_default && p.type === PropertyType.Date)

  return {
    currentWorkspaceProperties,
    currentFolderProperties,
    projectProperties,
    propertiesUsableInCurrentFolder,
    fromStore,
    filterByName,
    sortByName,
    dateProperties,
  }
}
